// services/captionService.ts
import { apiClient } from "@/services/apiClient";
import { API_CONFIG } from "@/config/api";
import { logger } from "@/utils/logger";

export interface CaptionGenerationResponse {
  message: string;
}

export interface SinglePostCaptionResponse {
  success: boolean;
  caption: string;
  postId: string;
}

// Async caption generation (returns job ID immediately)
export interface AsyncCaptionGenerationResponse {
  success: boolean;
  jobId: string;
  status: "queued";
  message: string;
}

// Sync caption generation (returns captions directly - deprecated)
export interface SyncCaptionGenerationResponse {
  success: boolean;
  captions: CaptionVariant[];
  altText: string;
  clientContext: CaptionClientContext;
  generationId: number;
  usage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

export interface CaptionVariant {
  id: string;
  text: string;
  caption: string;
  hashtags: string;
  characterCount: number;
  hasHashtags: boolean;
}

export interface CaptionClientContext {
  brandVoice: string;
  targetAudience: string;
  hashtags: string[];
  location: string;
  brandDescription: string;
  writingGuidelines: string;
}

class CaptionService {
  private readonly apiUrl: string;

  constructor(
    apiUrl: string = API_CONFIG.URL,
  ) {
    this.apiUrl = apiUrl;
  }

  // Bulk caption generation (existing)
  async generateCaptions(
    clientUuid: string,
  ): Promise<CaptionGenerationResponse> {
    try {
      const response = await apiClient.post(
        `/client/${clientUuid}/generate-captions`,
        {},
        {
          timeout: 120000,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response;
    } catch (error: any) {
      logger.error("Error generating captions:", error);

      if (apiClient.isAxiosError(error)) {
        if (error.code === "ECONNABORTED") {
          throw new Error("Caption generation timed out. Please try again.");
        }
      }

      // If it's already a simple Error (from apiClient), just re-throw it
      if (error instanceof Error) {
        throw error;
      }

      throw new Error("Failed to generate captions");
    }
  }

  // Single post caption generation
  async generateCaptionForPost(
    clientUuid: string,
    postId: string,
  ): Promise<SinglePostCaptionResponse> {
    logger.debug("Generating caption for post:", postId);

    try {
      const response = await apiClient.post(
        `/client/${clientUuid}/generate-caption/post/${postId}`,
        {},
        {
          timeout: 60000, // 60s for AI processing
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response;
    } catch (error: any) {
      logger.error("Error generating post caption:", error);

      if (apiClient.isAxiosError(error)) {
        if (error.code === "ECONNABORTED") {
          throw new Error("Caption generation timed out. Please try again.");
        }
      }

      // If it's already a simple Error (from apiClient), just re-throw it
      if (error instanceof Error) {
        throw error;
      }

      throw new Error("Failed to generate caption for post");
    }
  }

  /**
   * Generate captions for a post asynchronously via job system.
   * Returns immediately with a job_id that can be tracked via SSE.
   */
  async generateCaptionsAsync(
    clientUuid: string,
    postId: string,
    imageDescription: string,
    keywords: string[] = [],
  ): Promise<AsyncCaptionGenerationResponse> {
    logger.debug("Queueing async caption generation for post:", postId);

    try {
      const response = await apiClient.post<AsyncCaptionGenerationResponse>(
        `/client/${clientUuid}/posts/${postId}/generate-captions`,
        {
          image_description: imageDescription,
          keywords,
          async: true, // Request async processing
        },
        {
          timeout: 10000, // Short timeout - just queuing the job
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response;
    } catch (error: unknown) {
      logger.error("Error queueing caption generation:", error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error("Failed to queue caption generation");
    }
  }

  /**
   * Generate captions synchronously (deprecated - use generateCaptionsAsync).
   * This method waits for the full result, which may timeout for complex generations.
   */
  async generateCaptionsSync(
    clientUuid: string,
    postId: string,
    imageDescription: string,
    keywords: string[] = [],
  ): Promise<SyncCaptionGenerationResponse> {
    logger.debug("Generating captions synchronously for post:", postId);

    try {
      const response = await apiClient.post<SyncCaptionGenerationResponse>(
        `/client/${clientUuid}/posts/${postId}/generate-captions`,
        {
          image_description: imageDescription,
          keywords,
          async: false, // Request sync processing
        },
        {
          timeout: 120000, // 2 minutes for AI processing
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response;
    } catch (error: unknown) {
      logger.error("Error generating captions:", error);

      if (apiClient.isAxiosError(error)) {
        if (error.code === "ECONNABORTED") {
          throw new Error("Caption generation timed out. Please try again or use async mode.");
        }
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error("Failed to generate captions");
    }
  }
}

export const captionService = new CaptionService();
